import { TaskScheduleValue } from '../Components/CalendarSchedulerTypes';
import {
  TaskScheduleApi,
  TaskScheduleCreateApi,
  TaskScheduleExecution,
  TaskScheduleUpdateApi,
} from '../Services/TaskScheduleApiProviderTypes';
import { hasScheduleSlots, toUtcSchedule } from '../utils/calendarSchedule';
import { formatCalendarDate } from '../utils/formatDate';

// ============================================================
// Types
// ============================================================

export type TaskScheduleForm = {
  name: string;
  assistantKey: string;
  startDate: string;
  endDate: string;
  taskDefinition: string;
  schedule: TaskScheduleValue;
};

type Agent = {
  key: string;
  name: string;
};

export type TaskScheduleFormProps = {
  form: TaskScheduleForm;
  agents: Agent[];
  isDisabled?: boolean;
  onChange: (form: TaskScheduleForm) => void;
  savedSchedule?: TaskScheduleValue;
};

// ============================================================
// Constants
// ============================================================

export const INITIAL_FORM_STATE: TaskScheduleForm = {
  name: '',
  assistantKey: '',
  startDate: '',
  endDate: '',
  taskDefinition: '',
  schedule: {},
};

export const TASK_SCHEDULE_FIELD_MAP: Record<keyof TaskScheduleForm, keyof TaskScheduleApi> = {
  name: 'name',
  assistantKey: 'assistant_key',
  startDate: 'start_date',
  endDate: 'end_date',
  taskDefinition: 'task_definition',
  schedule: 'schedule',
};

// ============================================================
// Table columns
// ============================================================

export const taskScheduleColumns = [
  { name: 'ID', selector: (row: TaskScheduleApi) => row.id, sortable: false },
  { name: 'Name', selector: (row: TaskScheduleApi) => row.name, sortable: false },
  {
    name: 'Start Date',
    selector: (row: TaskScheduleApi) => formatCalendarDate(row.start_date),
    sortable: false,
  },
  {
    name: 'End Date',
    selector: (row: TaskScheduleApi) => formatCalendarDate(row.end_date) || '-',
    sortable: false,
  },
];

export const executionColumns = [
  { name: 'Id', selector: (row: TaskScheduleExecution) => row.id, sortable: false },
  {
    name: 'Created At',
    selector: (row: TaskScheduleExecution) => new Date(row.created_at).toLocaleString(),
    sortable: false,
  },
  { name: 'Status', selector: (row: TaskScheduleExecution) => row.status, sortable: false },
];

// ============================================================
// Validation
// ============================================================

export const validate = (
  form: TaskScheduleForm,
  showAlert: (type: 'fail' | 'success', message: string) => void,
): boolean => {
  const checks: [boolean, string][] = [
    [!form.name.trim(), 'Task schedule name is required'],
    [!form.assistantKey.trim(), 'Agent is required'],
    [!form.startDate.trim(), 'Start date is required'],
    [!form.endDate.trim(), 'End date is required'],
    [!form.taskDefinition.trim(), 'Task definition is required'],
    [!hasScheduleSlots(form.schedule), 'Schedule is required'],
  ];

  const failed = checks.find(([condition]) => condition);
  if (failed) {
    showAlert('fail', failed[1]);
    return false;
  }

  return true;
};

// ============================================================
// Form <-> API mapping
// ============================================================

type FormValue = string | TaskScheduleValue;
type ApiValue = string | TaskScheduleValue | null;

const sortedStringify = (val: FormValue | ApiValue): string => {
  if (Array.isArray(val)) return JSON.stringify(val.map(sortedStringify));
  if (val && typeof val === 'object') {
    return JSON.stringify(
      Object.keys(val)
        .sort()
        .reduce(
          (acc, key) => ({
            ...acc,
            [key]: sortedStringify((val as Record<string, FormValue>)[key]),
          }),
          {},
        ),
    );
  }
  return JSON.stringify(val);
};

const isEqual = (a: FormValue, b: FormValue): boolean => sortedStringify(a) === sortedStringify(b);

export const mapFormToApi = (form: TaskScheduleForm): TaskScheduleCreateApi => {
  const api = Object.entries(TASK_SCHEDULE_FIELD_MAP).reduce((acc, [formKey, apiKey]) => {
    (acc as Record<keyof TaskScheduleCreateApi, ApiValue>)[apiKey as keyof TaskScheduleCreateApi] =
      form[formKey as keyof TaskScheduleForm];
    return acc;
  }, {} as TaskScheduleCreateApi);

  if (api.schedule) {
    api.schedule = toUtcSchedule(api.schedule);
  }

  return api;
};

export const getChangedFields = (
  initial: TaskScheduleForm,
  current: TaskScheduleForm,
): TaskScheduleUpdateApi => {
  const changes = Object.entries(TASK_SCHEDULE_FIELD_MAP).reduce((acc, [formKey, apiKey]) => {
    if (
      !isEqual(
        current[formKey as keyof TaskScheduleForm],
        initial[formKey as keyof TaskScheduleForm],
      )
    ) {
      (acc as Record<keyof TaskScheduleUpdateApi, ApiValue>)[
        apiKey as keyof TaskScheduleUpdateApi
      ] = current[formKey as keyof TaskScheduleForm];
    }
    return acc;
  }, {} as TaskScheduleUpdateApi);

  if (changes.schedule) {
    changes.schedule = toUtcSchedule(changes.schedule);
  }

  return changes;
};
